import React from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getSalonPublicTemplate } from '../../../theme/salonPublicTemplates';
import SalonTemplateThumbnail from './SalonTemplateThumbnail';


const SalonTemplateCard = ({
  templateKey,
  selected,
  disabled,
  compact,
  onSelect,
  inputRef,
}) => {
  const { t } = useTranslation();
  const entry = getSalonPublicTemplate(templateKey);
  const inputId = `salon-template-${compact ? 'featured' : 'catalog'}-${templateKey}`;
  const subtitle = t(`${entry.translationKey}.subtitle`);
  const recommendation = t(`${entry.translationKey}.recommendation`);
  const illustrativeLabel = t('salon_theme.template_picker.illustrative');

  const selectByIndex = (input, index) => {
    const fieldset = input.closest('fieldset');
    const radios = Array.from(fieldset?.querySelectorAll(
      `input[type="radio"][name="${input.name}"]`,
    ) || []);
    if (!radios.length) return;

    const normalizedIndex = (index + radios.length) % radios.length;
    const nextRadio = radios[normalizedIndex];
    onSelect(nextRadio.value);
    nextRadio.focus();
  };

  const handleKeyDown = (event) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(templateKey);
      return;
    }

    const fieldset = event.currentTarget.closest('fieldset');
    const radios = Array.from(fieldset?.querySelectorAll(
      `input[type="radio"][name="${event.currentTarget.name}"]`,
    ) || []);
    const index = radios.indexOf(event.currentTarget);
    if (index < 0) return;

    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      selectByIndex(event.currentTarget, index + 1);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      selectByIndex(event.currentTarget, index - 1);
    } else if (event.key === 'Home') {
      event.preventDefault();
      selectByIndex(event.currentTarget, 0);
    } else if (event.key === 'End') {
      event.preventDefault();
      selectByIndex(event.currentTarget, radios.length - 1);
    }
  };

  return (
    <Box
      component="label"
      htmlFor={inputId}
      data-testid={`salon-template-card-${templateKey}`}
      data-selected={String(selected)}
      style={{ minHeight: '44px' }}
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'stretch',
        p: compact ? 0.5 : 1.25,
        border: '2px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 2,
        backgroundColor: selected ? 'primary.50' : 'background.paper',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.56 : 1,
        '&:focus-within': {
          outline: '3px solid',
          outlineColor: 'primary.light',
          outlineOffset: '2px',
        },
      }}
    >
      <input
        ref={inputRef}
        id={inputId}
        type="radio"
        name={compact ? 'salon-template-featured' : 'salon-template-catalog'}
        value={templateKey}
        checked={selected}
        disabled={disabled}
        onChange={() => {
          if (!disabled) onSelect(templateKey);
        }}
        onKeyDown={handleKeyDown}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          opacity: 0,
        }}
      />
      <Stack spacing={compact ? 0.5 : 0.6} sx={{ width: '100%', minWidth: 0 }}>
        <SalonTemplateThumbnail
          templateKey={templateKey}
          label={illustrativeLabel}
          compact={compact}
        />
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 800 }}>
            {entry.name}
          </Typography>
          {selected ? (
            <CheckCircleRoundedIcon
              aria-label={t('salon_theme.template_picker.selected')}
              color="primary"
              fontSize="small"
            />
          ) : null}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
        {!compact ? (
          <>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontStyle: 'italic' }}
            >
              {recommendation}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {illustrativeLabel}
            </Typography>
          </>
        ) : null}
      </Stack>
    </Box>
  );
};

export default SalonTemplateCard;
